import QRCode from 'react-qr-code';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { translate } from '@/translation/translate.ts';
import { ConfigTranslatedText, EventConfiguration } from '@/eventConfig/types.ts';
import Handlebars from 'handlebars';
import { FieldStatus } from '@/form/FormProvider.tsx';
import { preprocessAlphabeticLists } from '@/translation/preprocessAlphabeticLists';

const markdownSanitizeSchema = {
  ...defaultSchema,
  // Keep raw HTML support narrow: only add underline tag for content configuration.
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    // Allow alphabetic ordered lists produced by content preprocessing.
    ol: [...((defaultSchema.attributes?.ol as string[] | undefined) ?? []), 'type', 'start'],
  },
};

if (!Handlebars.helpers.lastN) {
  Handlebars.registerHelper('lastN', (input: unknown, count: unknown) => {
    const value = `${input ?? ''}`;
    const parsedCount = Number(count);
    const safeCount = Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : 0;
    return safeCount ? value.slice(-safeCount) : '';
  });
}

if (!Handlebars.helpers.formatDate) {
  Handlebars.registerHelper('formatDate', (input: unknown, format: unknown) => {
    const value = `${input ?? ''}`;
    const outputFormat = `${format ?? ''}`.toUpperCase();

    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!isoDateMatch) {
      return value;
    }

    const [, year, month, day] = isoDateMatch;

    if (outputFormat === 'DD/MM/YYYY') {
      return `${day}/${month}/${year}`;
    }
    if (outputFormat === 'DD.MM.YYYY') {
      return `${day}.${month}.${year}`;
    }

    return value;
  });
}

type Template = ConfigTranslatedText | string | undefined;
type Data = {
  eventConfig: EventConfiguration | null;
  form: Record<string, FieldStatus>;
};

export const fromTemplate = (template: Template, data?: Data) => {
  if (!template) {
    return '';
  }

  template = translate(template);

  // "data" is accessed by: "value = form.<slideId>.<fieldName>", so lets present the data like this
  const formData = Object.keys(data?.form ?? {}).reduce((acc, curr) => {
    if (!data?.form) {
      return acc;
    }
    const slideId = data.form[curr].slideId;
    const value = data.form[curr].value.normalized;
    return { ...acc, [slideId]: { ...(acc[slideId] ?? {}), [curr]: value } };
  }, {} as any);

  const content = Handlebars.compile(template).call(null, {
    eventConfig: data?.eventConfig,
    form: formData,
  });

  const processedContent = preprocessAlphabeticLists(content);

  const components: Components = {
    a({ children, href, node, ..._props }) {
      if (typeof children !== 'string') {
        return;
      }
      const [text, classNames, ...componentProps] = (children ?? '').split(';');
      if (typeof href !== 'string') {
        return text;
      }

      let content = null;
      switch (classNames) {
        case 'qr-code':
          content = (
            <>
              <QRCode value={href} size={parseInt(componentProps[0]) || 256} />
              <span>{text}</span>
            </>
          );
          break;
        case 'gift':
          content = (
            <div className={classNames}>
              <a className="button" target={'_blank'} rel={'noreferrer noopener'} href={href}>
                {text}
              </a>
            </div>
          );
          break;
        default:
          content = text;
          break;
      }
      return (
        <a className={classNames} target={'_blank'} rel={'noreferrer noopener'} href={href}>
          {content}
        </a>
      );
    },
  };

  return (
    <ReactMarkdown components={components} rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}>
      {processedContent}
    </ReactMarkdown>
  );
};
